import { useMemo } from "react";
import useHome from "./useHome";
import useSite from "./useSite";
import { ALL_SITES_ID, isAllSites } from "../../utils/globalSite";

/**
 * Global site scope from the header selector (HomeContext + SiteContext).
 */
export default function useGlobalSite() {
  const {
    curSiteId,
    setCurSiteId,
    siteSelectorEnabled,
    siteSelectorShowAllOption,
    setSiteSelectorEnabled,
    setSiteSelectorShowAllOption,
    applySiteSelectorUi,
    curProjectId,
    sitesFromProjectId,
    projectList,
  } = useHome();
  const { siteList } = useSite();

  const siteOptions = useMemo(
    () =>
      (sitesFromProjectId?.[curProjectId] ?? []).filter(
        (s) => s?.site_id && s.isDeleted !== true && !s.deleted
      ),
    [sitesFromProjectId, curProjectId]
  );

  const selectedSite = useMemo(() => {
    if (isAllSites(curSiteId)) return null;
    return siteOptions.find((s) => s?.site_id === curSiteId) ?? null;
  }, [siteOptions, curSiteId]);

  const curSiteUid = useMemo(() => {
    if (isAllSites(curSiteId)) return null;
    const fromList = siteList?.find((s) => s.site_id === curSiteId);
    if (fromList?.id) return fromList.id;
    const fromOptions = siteOptions.find((s) => s?.site_id === curSiteId);
    return fromOptions?.id ?? fromOptions?.site_uid ?? null;
  }, [curSiteId, siteList, siteOptions]);

  const effectiveSiteId = useMemo(() => {
    if (!siteSelectorEnabled || isAllSites(curSiteId)) {
      return siteOptions[0]?.site_id ?? null;
    }
    return curSiteId;
  }, [siteSelectorEnabled, curSiteId, siteOptions]);

  const effectiveSiteUid = useMemo(() => {
    if (!effectiveSiteId) return null;
    const fromList = siteList?.find((s) => s.site_id === effectiveSiteId);
    if (fromList?.id) return fromList.id;
    const fromOptions = siteOptions.find((s) => s?.site_id === effectiveSiteId);
    return fromOptions?.id ?? fromOptions?.site_uid ?? null;
  }, [effectiveSiteId, siteList, siteOptions]);

  /** API body helper: pass site_id only when a single site is selected. */
  const apiSiteId = useMemo(() => {
    if (isAllSites(curSiteId)) return ALL_SITES_ID;
    return curSiteId;
  }, [curSiteId]);

  const isAllSitesSelected = isAllSites(curSiteId);

  const sitesReady = useMemo(
    () =>
      projectList != null &&
      typeof sitesFromProjectId === "object" &&
      Object.keys(sitesFromProjectId).length > 0,
    [projectList, sitesFromProjectId]
  );

  return {
    curSiteId,
    setCurSiteId,
    siteSelectorEnabled,
    siteSelectorShowAllOption,
    setSiteSelectorEnabled,
    setSiteSelectorShowAllOption,
    applySiteSelectorUi,
    siteOptions,
    projectList,
    sitesReady,
    selectedSite,
    curSiteUid,
    effectiveSiteId,
    effectiveSiteUid,
    apiSiteId,
    isAllSitesSelected,
    ALL_SITES_ID,
  };
}

export { ALL_SITES_ID, isAllSites };
